import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { myWorkAPI, ticketsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import {
  Loader2, CheckCircle2, Clock, AlertTriangle, ListTodo, ArrowRight, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import { avisarDependenciasPendientes, textoDeBloqueo } from '@/lib/dependenciasPendientes';
import DeclararAvance from '@/components/tickets/DeclararAvance';
import { TASK_STATUS_LABELS } from '@/components/tickets/constants';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

// El proximo estado ya no se inventa aca. Lo calcula el servidor con el catalogo de la capsula
// que corresponda a cada tarea —la bandeja cruza capsulas— y viaja en `siguiente_estado`.
//
// Antes esta pagina tenia su propia lista y decia `review`, que la API no acepta (es
// `in_review`): avanzar una tarea bloqueada devolvia 400 y el boton no hacia nada. Ademas
// mandaba de "en curso" a "bloqueada", que es lo contrario de avanzar.


/**
 * Si el aviso de esta tarea salio, y por donde.
 *
 * **Por que hace falta** (2026-08-22). `notified_at` significaba "se intento", no "llego": tres
 * tareas de un caso quedaron marcadas como avisadas y no salio un solo correo. Ahora el aviso es
 * un registro con su desenlace, y esto lo muestra donde la persona mira su trabajo.
 *
 * **Los tres desenlaces se ven distinto a proposito.** "No se pudo avisar" es algo para
 * arreglar; "solo en la app" es una preferencia que alguien eligio. Pintarlos igual esconde el
 * que hay que atender detras del que esta bien.
 */
function MarcaDeAviso({ aviso }) {
  const estado = aviso?.estado || 'sin_aviso';
  if (estado === 'sin_aviso') return null;

  const estilos = {
    entregado: { clase: 'bg-emerald-50 text-emerald-700 border-emerald-200', icono: '✓',
                 texto: aviso.canal ? `Avisado por ${aviso.canal}` : 'Avisado' },
    no_entregado: { clase: 'bg-red-50 text-red-700 border-red-200', icono: '⚠',
                    texto: 'No se pudo avisar' },
    solo_en_la_app: { clase: 'bg-zinc-100 text-zinc-600 border-zinc-200', icono: '●',
                      texto: 'Solo en la app' },
    pendiente: { clase: 'bg-amber-50 text-amber-700 border-amber-200', icono: '…',
                 texto: 'Aviso en curso' },
  }[estado] || { clase: 'bg-zinc-100 text-zinc-600 border-zinc-200', icono: '?', texto: estado };

  // El motivo va en el `title`: en la fila ocupa lugar y solo importa cuando algo fallo.
  const detalle = aviso.detalle
    ? `${estilos.texto} — ${aviso.detalle}`
    : estilos.texto;

  return (
    <span
      data-testid="marca-de-aviso"
      title={detalle}
      className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${estilos.clase}`}
    >
      {estilos.icono} {estilos.texto}
    </span>
  );
}


export default function MyWorkPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantId = user?.tenant_id;
  const [loading, setLoading] = useState(true);
  // **El historial, con el mismo interruptor que Mis Solicitudes.** Dos pantallas que contestan
  // la misma pregunta —"¿y lo que ya terminé?"— se manejan igual: una persona no tiene por qué
  // aprender dos gestos para lo mismo.
  const [verTerminadas, setVerTerminadas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [data, setData] = useState({ summary: {}, tasks: [], by_status: {} });
  const [casos, setCasos] = useState({ mios: [], sin_dueno: [], mis_roles: {} });
  // La cola de **tareas** de mis roles. Desde el 2026-10-01 una plantilla reparte solo su fase de
  // entrada, asi que el trabajo de las fases siguientes espera aca hasta que alguien lo tome.
  const [colaDeTareas, setColaDeTareas] = useState({ tareas: [], mis_roles: {} });
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [tomando, setTomando] = useState(null);
  const [avisos, setAvisos] = useState({ avisos: [], sin_leer: 0 });
  // La tarea que se esta devolviendo, y por que. El motivo es obligatorio: quien la tome despues
  // necesita saber que se descubrio, o vuelve a empezar de cero.
  const [devolviendo, setDevolviendo] = useState(null);
  const [motivoDevolucion, setMotivoDevolucion] = useState('');

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [res, resCasos, resCola, resAvisos] = await Promise.all([
        myWorkAPI.listTasks(tenantId, {
          ...(verTerminadas ? { incluir_terminadas: true } : {}),
          ...(busquedaAplicada ? { q: busquedaAplicada } : {}),
        }),
        myWorkAPI.listTickets(tenantId).catch(() => ({ data: { mios: [], sin_dueno: [], mis_roles: {} } })),
        // La cola de tareas no puede tumbar la pantalla: si falla, se ven las propias igual.
        myWorkAPI.listTareasSinDueno(tenantId).catch(() => ({ data: { tareas: [], mis_roles: {} } })),
        // **Solo lo de trabajo, y solo lo sin leer.** Esta pantalla es la del trabajo: los
        // avisos de lo que uno *pidió* —tu caso cambió de estado— no son trabajo de nadie y
        // ocupaban la mitad de arriba. Viven en la campana, junto con el historial de los ya
        // leídos. Acá queda lo pendiente de atender, corto.
        // La bandeja no puede tumbar la pantalla: si falla, se ven las tareas igual.
        myWorkAPI.listAvisos(tenantId, { titulo: 'trabajo', solo_sin_leer: true, limit: 6 })
          .catch(() => ({ data: { avisos: [], sin_leer: 0 } })),
      ]);
      setData(res.data || { summary: {}, tasks: [], by_status: {} });
      setCasos(resCasos.data || { mios: [], sin_dueno: [], mis_roles: {} });
      setColaDeTareas(resCola.data || { tareas: [], mis_roles: {} });
      setAvisos(resAvisos.data || { avisos: [], sin_leer: 0 });
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudieron cargar tus tareas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [tenantId, verTerminadas, busquedaAplicada]);

  const marcarLeido = async (aviso) => {
    if (aviso.leido_at) return;
    // Optimista: la marca es del que mira, y esperar al servidor para tachar un renglon propio
    // se siente roto. Si falla, el proximo refresco lo devuelve a su lugar.
    setAvisos(prev => ({
      ...prev,
      sin_leer: Math.max(0, (prev.sin_leer || 0) - 1),
      avisos: prev.avisos.map(a =>
        a.aviso_id === aviso.aviso_id ? { ...a, leido_at: new Date().toISOString() } : a),
    }));
    try { await myWorkAPI.marcarAvisoLeido(tenantId, aviso.aviso_id); } catch { /* vuelve al refrescar */ }
  };

  /**
   * Declarar cuanto de la tarea esta hecho. **Un clic, y recarga.**
   *
   * Va en esta pantalla y no solo en el dialogo del que planifica: un campo que vive alla no lo
   * carga nunca quien hace el trabajo, y el indicador nace vacio. Es la misma razon por la que el
   * bloqueo y el aviso bajaron hasta aca.
   */
  const declararAvance = async (task, pct) => {
    if (!task?.task_id || !task?.ticket_id || !task?.capsule_id) return;
    setBusyTaskId(task.task_id);
    try {
      await ticketsAPI.patchTask(tenantId, task.capsule_id, task.ticket_id, task.task_id, {
        avance_pct: pct,
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo guardar el avance');
    } finally {
      setBusyTaskId(null);
    }
  };

  const patchStatus = async (task) => {
    if (!task?.task_id || !task?.ticket_id || !task?.capsule_id) return;
    const next = task.siguiente_estado;
    if (!next || next === task.status) return;
    setBusyTaskId(task.task_id);
    try {
      const res = await ticketsAPI.patchTask(tenantId, task.capsule_id, task.ticket_id, task.task_id, { status: next });
      // **Primero lo que esta mal, despues la confirmacion.** Esta pantalla decia "Estado
      // actualizado" y tiraba los `warnings`: se podia cerrar una tarea por encima de su
      // predecesora y el sistema felicitaba. Un fallo que se disfraza de exito es peor que uno
      // visible, porque nadie lo busca.
      if (!avisarDependenciasPendientes(res)) toast.success('Estado actualizado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || 'Error al actualizar');
    } finally {
      setBusyTaskId(null);
    }
  };

  const tomarCaso = async (t) => {
    setTomando(t.ticket_id);
    try {
      await ticketsAPI.tomar(tenantId, t.capsule_id, t.ticket_id);
      toast.success('El caso es tuyo');
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo tomar');
    } finally {
      setTomando(null);
    }
  };

  const tomarTarea = async (t) => {
    setTomando(t.task_id);
    try {
      await ticketsAPI.tomarTarea(tenantId, t.capsule_id, t.ticket_id, t.task_id);
      toast.success('La tarea es tuya');
      await load();
    } catch (e) {
      // El 409 es el caso real de dos personas del mismo rol apretando a la vez, y el mensaje del
      // servidor dice quien la tomo: se muestra tal cual en vez de un "no se pudo" generico.
      toast.error(formatApiError(e) || 'No se pudo tomar la tarea');
      await load();
    } finally {
      setTomando(null);
    }
  };

  const confirmarDevolucion = async () => {
    const t = devolviendo;
    if (!t) return;
    await ticketsAPI.devolverTarea(tenantId, t.capsule_id, t.ticket_id, t.task_id, {
      motivo: motivoDevolucion.trim(),
    });
    toast.success(`Volvio a la cola de ${t.rol}`);
    setDevolviendo(null);
    setMotivoDevolucion('');
    await load();
  };

  // **El caso entero, en su ficha.** Iba sin `tab`, y el default de `TicketsPage` es `detail`
  // solo si no hay tarea en la URL — asi que se explicita: es donde viven la descripcion, el
  // analisis y el workspace con los artefactos, que es la informacion que la gente no encontraba.
  const abrirCaso = (t) => {
    navigate(`/tickets?${new URLSearchParams({ ticket: t.ticket_id, capsule: t.capsule_id, tab: 'detail' })}`);
  };

  // Antes esto dejaba a la persona sobre una tarjeta de 200 px del kanban, no sobre la tarea.
  const abrirTarea = (task) => {
    navigate(`/tickets/${task.ticket_id}/tasks/${task.task_id}?capsule=${task.capsule_id}`);
  };

  const summary = data.summary || {};
  const tasks = data.tasks || [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="my-work-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#09090B] tracking-tight">Mi trabajo</h1>
        <p className="text-sm text-zinc-500 mt-1">Tareas asignadas a {user?.full_name || user?.email}</p>
      </div>

      {/* **Cuatro secciones y nada que las explique.** La pantalla tiene «mis casos», «sin dueño en
          mis roles», «tareas disponibles en mis roles» y «mis tareas», y la diferencia entre las dos
          del medio no se deduce del título. El manual lo cuenta, pero el manual **sólo lo ven admin
          y supervisor**, que son justamente los que no trabajan acá. */}
      <QueSeConfiguraAca
        testId="ayuda-mi-trabajo"
        queEs={<>Todo lo que te toca, cruzando las áreas en las que trabajás. Una persona tiene un solo día, aunque trabaje en tres cápsulas.</>}
        paraQue={<>Contestar <b>qué hago primero</b> sin abrir caso por caso. El orden no es por lo último que se tocó: arriba va lo que vence antes y lo que está atrasado.</>}
        aQueAfecta={
          <>
            Las cuatro secciones no son lo mismo:
            <ul className="mt-1 space-y-0.5 list-disc pl-4">
              <li><b>Mis casos</b> — sos el dueño del caso entero, no de una tarea.</li>
              <li><b>Sin dueño en mis roles</b> — casos que nadie tomó y que tu rol atiende. Tomarlos es tuyo.</li>
              <li><b>Tareas disponibles en mis roles</b> — tareas sueltas de una fase que ya arrancó, sin dueño. Las tomás de la cola.</li>
              <li><b>Mis tareas</b> — las que ya tenés asignadas.</li>
            </ul>
          </>
        }
        ejemplo={<>Si una tarea dice <b>«esperando a otra tarea»</b>, podés avanzarla igual: el aviso no impide, dice que te estás solapando. Y si dice <b>«gastaste más de lo que avanzaste»</b>, conviene hablar con quien maneja el plan antes de seguir.</>}
        ojo={<>Declarar el avance es <b>informar</b>, no comprometerse: nadie te va a pedir que cumplas el porcentaje que pusiste. Lo que el área necesita saber es cuándo una estimación quedó corta, y eso sólo lo sabés vos.</>}
      />

      {/* **Buscar y ver lo terminado.** Sin esto la pantalla sólo contesta "¿qué tengo que
          hacer?" y no "¿qué hice?" — y desde que las tareas de casos cerrados se ocultan, lo
          hecho no estaba en ninguna parte. Mismo gesto que el de Mis Solicitudes, a propósito. */}
      <div className="flex flex-wrap items-center gap-2">
        <form
          onSubmit={(e) => { e.preventDefault(); setBusquedaAplicada(busqueda.trim()); }}
          className="flex items-center gap-1"
        >
          <input
            type="search"
            data-testid="buscar-tarea"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por tarea o numero de caso"
            className="h-8 w-60 text-xs border border-brand-border rounded-sm px-2"
          />
          {busquedaAplicada && (
            <button
              type="button"
              onClick={() => { setBusqueda(''); setBusquedaAplicada(''); }}
              className="text-[11px] text-zinc-500 hover:text-brand-primary"
            >
              limpiar
            </button>
          )}
        </form>
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-600" data-testid="ver-terminadas">
          <input
            type="checkbox"
            checked={verTerminadas}
            onChange={(e) => setVerTerminadas(e.target.checked)}
          />
          Ver tambien lo terminado
        </label>
      </div>

      {/* **La bandeja de avisos.** Todo lo que se le aviso a esta persona, haya salido por un
          canal o no. Es lo que hace posible que alguien elija enterarse acá y no por correo: el
          aviso existe igual, y la preferencia solo decide si además se intenta entregarlo. */}
      {(avisos.avisos || []).length > 0 && (
        <Card className="rounded-sm border-brand-border" data-testid="bandeja-de-avisos">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600">
                Avisos de tu trabajo
              </h2>
              <span className="text-[10px] text-zinc-400">
                El historial y lo de tus pedidos, en la campana
              </span>
            </div>
            <div className="space-y-1.5">
              {(avisos.avisos || []).map((a) => (
                <button
                  key={a.aviso_id}
                  type="button"
                  onClick={() => marcarLeido(a)}
                  data-testid={`aviso-${a.aviso_id}`}
                  className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${
                    a.leido_at
                      ? 'border-zinc-100 bg-white text-zinc-500'
                      : 'border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {!a.leido_at && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />}
                    <span className={`text-xs truncate ${a.leido_at ? '' : 'font-medium'}`}>
                      {a.asunto}
                    </span>
                    <MarcaDeAviso aviso={a.entrega} />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{a.texto}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: 'pending', label: 'Pendientes', icon: ListTodo, color: 'text-zinc-600' },
          { key: 'in_progress', label: 'En curso', icon: Clock, color: 'text-blue-600' },
          { key: 'overdue', label: 'Atrasadas', icon: AlertTriangle, color: 'text-red-600' },
          { key: 'due_soon', label: 'Por vencer', icon: CheckCircle2, color: 'text-amber-600' },
          // **La alerta que llega a tiempo.** «Atrasada» avisa cuando la fecha ya pasó; esto avisa
          // mientras todavía se puede hacer algo. El subtítulo dice sobre cuántas se pudo medir,
          // porque un cero acá puede significar «todo bien» o «nadie declaró nada», y son dos
          // cosas muy distintas.
          {
            key: 'consumio_mas_de_lo_que_avanzo',
            label: 'Gastaron de más',
            icon: AlertTriangle,
            color: 'text-brand-danger',
            sub: `medible en ${summary.medible_el_desvio_en ?? 0}`,
            ayuda: 'Tareas donde se gastaron más horas de las que corresponden a lo avanzado. Hace falta que la tarea esté estimada y que alguien haya declarado su avance.',
          },
        ].map(({ key, label, icon: Icon, color, sub, ayuda }) => (
          <Card key={key} className="rounded-sm border-brand-border" title={ayuda}>
            <CardContent className="p-4 flex items-center gap-3">
              <Icon className={`w-5 h-5 ${color}`} />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
                <p className="text-xl font-semibold" data-testid={`resumen-${key}`}>{summary[key] ?? 0}</p>
                {sub && <p className="text-[10px] text-zinc-400">{sub}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mis casos: donde soy el propietario. Hasta el 2026-08-14 esta bandeja era solo de
          tareas, asi que quien tenia un caso a cargo no tenia donde verlo. */}
      {!loading && casos.mios.length > 0 && (
        <section data-testid="mis-casos">
          <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600 mb-2">
            Mis casos ({casos.mios.length})
          </h2>
          <div className="space-y-2">
            {casos.mios.map((t) => (
              <Card key={t.ticket_id} className="rounded-sm border-brand-border cursor-pointer hover:border-brand-primary/40"
                onClick={() => abrirCaso(t)} data-testid={`mi-caso-${t.ticket_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    {/* Nombres y no ids: `sistemas`, `in_progress` y `DES.BUG_APLICACION` crudos
                        eran lo que se leia. Los resuelve el servidor, que es el que conoce el
                        catalogo de cada capsula —la bandeja cruza capsulas—. Cae al id si la
                        capsula no declara el valor, que es informacion y no un defecto. */}
                    <p className="text-xs text-zinc-500 truncate">
                      {t.ticket_id} · {t.capsule_label || t.capsule_id}
                      {(t.intent_label || t.intent) ? ` · ${t.intent_label || t.intent}` : ''}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {t.status_label || t.status}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* La cola del rol. **No es opcional**: el caso nace con rol y sin persona, asi que sin
          esta seccion una unidad recien creada no aparece en la bandeja de nadie — que es la
          leccion del 2026-08-12 y la que este diseño reintroduciria si la pantalla faltara. */}
      {!loading && casos.sin_dueno.length > 0 && (
        <section data-testid="cola-de-mi-rol">
          <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600 mb-2">
            Sin dueño en mis roles ({casos.sin_dueno.length})
          </h2>
          <div className="space-y-2">
            {casos.sin_dueno.map((t) => (
              <Card key={t.ticket_id} className="rounded-sm border-amber-200 bg-amber-50/40">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => abrirCaso(t)}>
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {t.ticket_id} · {t.capsule_label || t.capsule_id}
                      {' · rol '}{t.rol_propietario_label || t.rol_propietario}
                    </p>
                  </div>
                  <Button size="sm" className="h-8 text-xs rounded-sm bg-brand-primary hover:bg-[#001F6D] text-white shrink-0"
                    disabled={tomando === t.ticket_id} onClick={() => tomarCaso(t)}
                    data-testid={`tomar-${t.ticket_id}`}>
                    {tomando === t.ticket_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Tomar'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* La cola de **tareas** de mis roles. Es la otra mitad de la decision de no repartir las
          fases futuras: si la cola no se muestra, el trabajo de la fase que se abre no aparece en
          la bandeja de nadie — la leccion del 2026-08-12, una unidad mas abajo.

          **Solo trae lo de la fase en curso**, asi que todo lo que aparece se puede empezar hoy.
          Y trae con que decidir: vencimiento, prioridad y antiguedad. La cola de casos nacio
          mostrando titulo, numero, capsula y rol, y se elegia a ciegas que tomar. */}
      {!loading && (colaDeTareas.tareas || []).length > 0 && (
        <section data-testid="cola-de-tareas-de-mi-rol">
          <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600 mb-1">
            Tareas disponibles en mis roles ({colaDeTareas.tareas.length})
          </h2>
          <p className="text-xs text-zinc-500 mb-2">
            Nadie las tiene todavia. Se pueden empezar: su fase esta en curso.
          </p>
          <div className="space-y-2">
            {colaDeTareas.tareas.map((t) => (
              <Card key={t.task_id} className="rounded-sm border-sky-200 bg-sky-50/40">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => abrirTarea(t)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium truncate">{t.title}</p>
                      <Badge variant="outline" className="text-[10px] rounded-sm border-sky-300 text-sky-800">
                        rol {t.rol}
                      </Badge>
                      {t.phase_label && (
                        <span className="text-[10px] text-zinc-500">fase {t.phase_label}</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 truncate mt-0.5">
                      {t.ticket_id} — {t.ticket_title}
                    </p>
                    {/* **Un guion es honesto; un cero es una afirmacion.** Lo que no se sabe no se
                        rellena: sin compromiso cargado no se escribe una fecha inventada. */}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-600 flex-wrap">
                      <span title="Fecha comprometida del caso">
                        {'vence '}
                        {t.ticket_fecha_hasta
                          ? String(t.ticket_fecha_hasta).slice(0, 10)
                          : (t.ticket_fecha_hasta_origen === 'sin_sla' ? 'sin compromiso' : '—')}
                      </span>
                      <span title="Prioridad del caso">
                        {'prioridad '}{t.ticket_priority || '—'}
                      </span>
                      <span title="Cuanto hace que la tarea existe">
                        {t.antiguedad_dias === null || t.antiguedad_dias === undefined
                          ? 'antiguedad —'
                          : `espera ${t.antiguedad_dias} d`}
                      </span>
                      <span title="Esfuerzo estimado de la tarea">
                        {'estimado '}
                        {t.estimate_hours === null || t.estimate_hours === undefined
                          ? '—'
                          : `${t.estimate_hours} h`}
                      </span>
                    </div>
                  </div>
                  <Button size="sm" className="h-8 text-xs rounded-sm bg-brand-primary hover:bg-[#001F6D] text-white shrink-0"
                    disabled={tomando === t.task_id} onClick={() => tomarTarea(t)}
                    data-testid={`tomar-tarea-${t.task_id}`}>
                    {tomando === t.task_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Tomar'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600">
        Mis tareas
      </h2>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : !tasks.length ? (
        <Card className="rounded-sm border-dashed border-brand-border">
          <CardContent className="p-10 text-center text-sm text-zinc-500">
            No tienes tareas asignadas. Cuando te asignen trabajo en un ticket, aparecera aqui.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <Card
              key={task.task_id}
              className={`rounded-sm border-brand-border cursor-pointer hover:border-brand-primary/40 transition-colors ${task.overdue ? 'border-red-200 bg-red-50/30' : ''}`}
              data-testid={`my-task-${task.task_id}`}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0" onClick={() => abrirTarea(task)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && abrirTarea(task)}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-medium text-sm truncate">{task.title}</span>
                    {/* **Una autorización no es una tarea más.** En una lista de veinte, sin
                        distintivo, la que espera la decisión de una persona se lee igual que la
                        que espera que alguien trabaje — y la primera bloquea a otro. */}
                    {task.task_type === 'autorizacion' && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-900">
                        <Stamp className="w-3 h-3 mr-1" />Tu autorización
                      </Badge>
                    )}
                    {task.overdue && <Badge variant="destructive" className="text-[10px]">Atrasada</Badge>}
                    {task.due_soon && !task.overdue && <Badge className="text-[10px] bg-amber-100 text-amber-800">Por vencer</Badge>}
                    {/* **Lo que la traba, donde se trabaja.** Esta marca existia solo en el kanban
                        del caso: quien tiene la tarea era el unico que no veia que estaba esperando
                        a otra. Mismo `data-testid` que en `TaskCard` a proposito — si las dos se
                        desincronizan, un test lo encuentra. */}
                    {(task.bloqueada_por || []).length > 0 && (
                      <Badge
                        className="text-[10px] bg-zinc-200 text-zinc-700"
                        title={textoDeBloqueo(task)}
                        data-testid={`task-blocked-${task.task_id}`}
                      >
                        Esperando a otra tarea
                      </Badge>
                    )}
                    {/* Sin `font-mono`: dejo de ser un id. El nombre lo manda el servidor con el
                        catalogo de la capsula de **esa** tarea; `TASK_STATUS_LABELS` queda como
                        respaldo de los ids de plataforma, que es para lo que existe. */}
                    <Badge variant="outline" className="text-[10px]">
                      {task.status_label || TASK_STATUS_LABELS[task.status] || task.status}
                    </Badge>
                    <MarcaDeAviso aviso={task.aviso} />
                    {/* **De donde salio.** Una tarea que aparece sin origen desconcierta: la
                        pide un agente en medio de un turno y en la bandeja se lee como caida
                        del cielo. El backend lo deriva con `clasificar_actor`. */}
                    {task.pedida_por === 'agent' && (
                      <Badge variant="outline" className="text-[10px] border-brand-primary/40 text-brand-primary">
                        La pidió un agente
                      </Badge>
                    )}
                  </div>
                  {/* El motivo, que es lo que explica la tarea. Lo escribe quien la crea y hasta
                      hoy solo se veia entrando a la tarea. */}
                  {task.description && (
                    <p className="text-xs text-zinc-600 line-clamp-2 mb-1">{task.description}</p>
                  )}
                  <p className="text-xs text-zinc-500 truncate">
                    {task.ticket_id} · {task.ticket_title || 'Ticket'} · {task.capsule_id}
                    {task.phase ? ` · fase ${task.phase}` : ''}
                    {task.planned_end ? ` · fin ${task.planned_end}` : ''}
                  </p>
                  {/* **La prioridad del caso ya viajaba y nadie la dibujaba.** Entre veinte tareas
                      sin atraso no habia ninguna señal de que va primero: el orden de la lista es
                      por `updated_at`, o sea por lo ultimo que alguien toco. Y el compromiso con
                      quien pidio —`fecha_hasta` del caso— es lo que de verdad apura, no el fin
                      planificado de la tarea. */}
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-zinc-600 flex-wrap">
                    {task.ticket_priority && (
                      <span title="Prioridad del caso">prioridad {task.ticket_priority}</span>
                    )}
                    {task.ticket_fecha_hasta && (
                      <span title="Fecha comprometida con quien pidió el caso">
                        comprometido {String(task.ticket_fecha_hasta).slice(0, 10)}
                      </span>
                    )}
                  </div>
                  {/* **Lo hecho y lo gastado, juntos y en la pantalla de quien trabaja.** El clic
                      no abre nada: declarar tiene que costar un clic o no se declara. Y el `stopPropagation`
                      porque el contenedor abre la tarea. */}
                  <div className="mt-1.5" onClick={(e) => e.stopPropagation()} role="presentation">
                    <DeclararAvance
                      task={task}
                      disabled={busyTaskId === task.task_id}
                      onDeclarar={(pct) => declararAvance(task, pct)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs rounded-sm"
                    disabled={busyTaskId === task.task_id || !task.siguiente_estado}
                    onClick={() => patchStatus(task)}
                    data-testid={`my-task-advance-${task.task_id}`}
                    title={task.siguiente_estado
                      ? `Pasar a ${task.siguiente_estado_label || TASK_STATUS_LABELS[task.siguiente_estado] || task.siguiente_estado}`
                      : 'La tarea ya termino'}
                  >
                    {busyTaskId === task.task_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Avanzar'}
                  </Button>
                  {/* **«Esto no es mío».** Hasta hoy no existia: el unico camino era salir de la
                      pantalla y pedirselo al dueño del caso por otro canal, con la tarea ocupando
                      lugar en la bandeja mientras tanto. Solo aparece si la tarea tiene un rol: sin
                      rol no hay cola a la que devolverla. */}
                  {task.rol && (
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-zinc-500"
                      onClick={() => setDevolviendo(task)}
                      data-testid={`devolver-tarea-${task.task_id}`}>
                      No es mía
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => abrirCaso(task)}>
                    Ver el caso
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => abrirTarea(task)}>
                    Abrir la tarea <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-zinc-400">
        Tambien puedes ver el tablero completo en <Link to="/tickets" className="text-brand-primary hover:underline">Tickets</Link>.
      </p>

      {/* Devolver no es destructivo —la tarea no se pierde, vuelve a su cola— pero **cambia de manos
          trabajo de otra persona**, asi que se confirma. El diálogo dice adonde vuelve y exige el
          motivo: el boton queda apagado hasta que haya texto, que es mas honesto que aceptar y
          rechazar despues. */}
      <ConfirmActionDialog
        open={!!devolviendo}
        onOpenChange={(abierto) => { if (!abierto) { setDevolviendo(null); setMotivoDevolucion(''); } }}
        title="Devolver la tarea a su cola"
        description={devolviendo
          ? `«${devolviendo.title}» vuelve a la cola del rol ${devolviendo.rol}: deja de ser tuya y la puede tomar cualquiera de ese rol. Se les avisa.`
          : ''}
        confirmText="Devolver"
        confirmDisabled={motivoDevolucion.trim().length < 3}
        onConfirm={confirmarDevolucion}
      >
        <div className="space-y-1">
          <label className="text-xs font-medium text-zinc-700" htmlFor="motivo-devolucion">
            Por que la devolves
          </label>
          <Textarea
            id="motivo-devolucion"
            data-testid="motivo-devolucion"
            value={motivoDevolucion}
            onChange={(e) => setMotivoDevolucion(e.target.value)}
            placeholder="Que averiguaste, o por que no te corresponde. Lo lee quien la tome despues."
            className="rounded-sm text-sm"
            rows={3}
          />
          <p className="text-[11px] text-zinc-500">
            Queda en el historial del caso. Sin esto, quien la tome arranca con la misma informacion
            que vos tenias cuando decidiste que no era tuya.
          </p>
        </div>
      </ConfirmActionDialog>
    </div>
  );
}
